import { getAuth } from "firebase/auth";
import { DatabaseReference, get, getDatabase, ref, remove, update } from "firebase/database";
import Environment from "../../utils/Environement";
import { EstablishmentPaymentDialogState } from "../../view/Obypay/_interfaces/obypay";
import type { Listener, ObyPayMaster, ObyPayUser, paymentMapping } from "../api/ObyPayApi";
import ObyPayApi from "../api/ObyPayApi";
import { StoreActionType } from "../constant/ActionConstant";
import AppDispatcher from "../dispatcher/AppDispatcher";
import Service from "../model/service/Service";

type PaymentMapping = {
    [key: string]: string;
}

export type PaymentsMapped = {
    pos: PaymentMapping;
    online: PaymentMapping;
}

class ObyPayActionCreator {
    private api: ObyPayApi

    constructor() {
        this.api = new ObyPayApi(Environment.isDev, Environment.isPopina);
    }

    getUserUid(): string {
        const auth = getAuth()
        const user = auth.currentUser
        if (!user) {
            throw new Error('User not authenticated')
        }
        return user.uid
    }

    async loadService(): Promise<void> {
        const userUid = this.getUserUid()
        try {
            const reference = this.getDatabaseThirdPartysReference(userUid)
            const snapshot = await get(reference)
            if (snapshot.exists()) {
                const serviceUser = new Service(snapshot.val())
                this.dispatchServiceUpdate(serviceUser)
            }
        } catch (error) {
            throw this.handleError(error)
        }
    }

    private createServiceUser(user: ObyPayUser | ObyPayMaster, token: string, signatureKey: string, listenerId: string): Service {
        const userUid = this.getUserUid()
        return new Service({
            service: 'obypay',
            accessToken: token,
            isActive: true,
            signatureKey: signatureKey,
            accountName: user.name,
            uid: userUid
        });
    }

    private createEmptyServiceUser(): Service {
        return new Service({
            service: 'obypay',
            accessToken: '',
            isActive: false,
            signatureKey: '',
            accountName: '',
        });
    }

    private dispatchServiceUpdate(serviceUser: Service): void {
        AppDispatcher.dispatch({
            type: StoreActionType.SET_OBYPAY_SERVICE,
            data: serviceUser
        });
    }

    private handleError(error: unknown, message?: string): string | unknown {
        if (message) {
            return new Error(message)
        }
        if (error instanceof Error) {
            return error.message
        }
        return error
    }

    private getDatabaseThirdPartysReference(userUid: string): DatabaseReference {
        const database = getDatabase()
        return ref(database, `thirdpartys/${userUid}/obypay/`)
    }

    private async updateServiceUserInDatabase(userUid: string, serviceUser: Service): Promise<void> {
        const reference = this.getDatabaseThirdPartysReference(userUid);
        await update(reference, serviceUser.toJS());
    }

    private async removeServiceUserFromDatabase(userUid: string): Promise<void> {
        const reference = this.getDatabaseThirdPartysReference(userUid)
        await remove(reference)
    }

    async validateObyPayUser(token: string, signatureKey: string): Promise<ObyPayUser | ObyPayMaster> {
        const userUid = this.getUserUid()
        try {
            await this.deleteOrderListeners(token)
            const user = await this.api.authenticateObyPayUser(token)
            const listenerId = await this.createOrderListener(token)
            const service = this.createServiceUser(user, token, signatureKey, listenerId)
            await this.updateServiceUserInDatabase(userUid, service)
            this.dispatchServiceUpdate(service)
            return user
        } catch (error) {
            throw this.handleError(error)
        }
    }

    async setPaymentMapping(token: string, mapping: paymentMapping): Promise<void> {
        try {
            await this.mapRestaurantPaymentMethods(token, mapping)
        } catch (error) {
            throw this.handleError(error)
        }
    }

    formatPaymentMappingForRequest(paymentMapping: EstablishmentPaymentDialogState["paymentMapping"]): PaymentsMapped {
        const keys = Object.keys(paymentMapping)
        const paymentsMapped: PaymentsMapped = {
            pos: {},
            online: {}
        }
        for (const paymentKey of keys) {
            const [paymentGroup, paymentItem] = paymentKey.split('_')
            if (paymentGroup === 'online') {
                paymentsMapped.online[paymentItem] = paymentMapping[paymentKey]
            } else if (paymentGroup === 'pos') {
                paymentsMapped.pos[paymentItem] = paymentMapping[paymentKey]
            }
        }
        return paymentsMapped
    }

    async mapRestaurantPaymentMethods(token: string, mapping: paymentMapping): Promise<void> {
        try {
            await this.api.mapObypayPaiementWithPOSPaiement(token, mapping)
        } catch (error) {
            throw this.handleError(error)
        }
    }

    async createOrderListener(token: string): Promise<string> {
        const userUid = this.getUserUid()
        try {
            const listener = await this.api.createOrderListener(token, userUid)
            return listener
        } catch (error) {
            throw this.handleError(error)
        }

    }

    async getAllListeners(token: string): Promise<Listener[]> {
        try {
            const listeners = await this.api.getAllListeners(token)
            return listeners
        } catch (error) {
            throw this.handleError(error)
        }
    }

    async deleteOrderListeners(token: string): Promise<void> {
        try {
            const allListeners = await this.getAllListeners(token)
            for (const { id } of allListeners) {
                await this.api.deleteOrderListener(token, id)
            }
        } catch (error) {
            throw this.handleError(error)
        }
    }

    async disconnectService(token: string): Promise<void> {
        const userUid = this.getUserUid()
        try {
            await this.deleteOrderListeners(token)
            await this.removeServiceUserFromDatabase(userUid)
            const emptyService = this.createEmptyServiceUser()
            this.dispatchServiceUpdate(emptyService)
        } catch (error) {
            throw this.handleError(error)
        }
    }
}

const obyPayActionCreator = new ObyPayActionCreator()
export default obyPayActionCreator
